import { Component, OnInit, Input, SimpleChanges, ViewChild, EventEmitter, Output, OnDestroy, Inject } from '@angular/core';
import { Train } from '../../../trains/models/train';
import { TrainSchedule, TrainWithSchedule} from '../../models/train-schedule';
import { TranslateService } from '@ngx-translate/core';
import { TrainScheduleEditComponent } from '../train-schedule-edit/train-schedule-edit.component';
import { TimetableEntry } from '../../models/timetable-entry';
import { Subscription } from 'rxjs';
import { ReferenceArea } from '../../../reference-areas/models/reference-area';
import { ConfirmationDialogService } from '../../../../shared/services/confirmation-dialog.service';
import { TrainScheduleExportService } from '../../services/train-schedule-export.service';

@Component({
  selector: 'app-train-schedule',
  template: require('./train-schedule.component.html'),
  styles: [require('./train-schedule.component.sass')]
})
export class TrainScheduleComponent implements OnInit, OnDestroy {
  
  @Input() scheduleDate: Date;
  @Input() trains: Train[];
  @Input() trainSchedules: TrainSchedule[];
  @Input() referenceAreas: ReferenceArea[];
  @Input() timetable: TimetableEntry[];
  @Input() projectId: number;

  @Output() saveTrainScheduleEntry = new EventEmitter<TrainSchedule>();
  @Output() deleteTrainScheduleEntry = new EventEmitter<TrainSchedule>();

  @ViewChild(TrainScheduleEditComponent) trainScheduleEdit: TrainScheduleEditComponent;

  trainsWithSchedules: TrainWithSchedule[];
  currentTrain: TrainWithSchedule | undefined;

  trainIdentifiers: {
    fieldName: string,
    displayedName: string
  }[] = [{
    fieldName: 'relativeId',
    displayedName:  'I18N_SCHEDULING.RELATIVE_ID'
  },
  {
    fieldName: 'officialId',
    displayedName:  'I18N_SCHEDULING.OFFICIAL_ID'
  }];

  currentTrainIdentifier: {
    fieldName: string,
    displayedName: string
  }; 

  trainIdentifier: {
    fieldName: string,
    displayedName: string
  }

  tableConfiguration: any = {
    paginate: true,
    sortableMode: 'multiple',
    defaultPagination: 250,
    itemsPerPageOptions: [50,100,250,500],
    exportFilename: 'timetable'
  }

  columns: any[] ;

  languageChangeSubscription: Subscription;

  constructor(
    private translate: TranslateService,
    private confirmationDialogService: ConfirmationDialogService,
    private trainScheduleExportService: TrainScheduleExportService,
    @Inject('TrainScheduleExportFactory') private trainScheduleExportFactory: any
) {
    this.languageChangeSubscription = this.translate.onLangChange.subscribe(() => {
      this.refreshTrainScheduleTable();
    })
  }

  ngOnInit(): void {
    this.trainIdentifier = this.trainIdentifiers && this.trainIdentifiers[0];
    this.refreshTrainScheduleTable();
  }

  refreshTrainScheduleTable = () => {

    if (this.trainIdentifier) {
      this.columns = [{
        header: this.translate.instant(this.trainIdentifier.displayedName),
        field: this.trainIdentifier.fieldName,
        type: 'text',
        width: '200px'
      }, {
        header: this.translate.instant('I18N_SCHEDULING.SERVICES'),
        field: 'services',
        type: 'services'
      }, {
        header: this.translate.instant('I18N_SCHEDULING.ACTIONS'),
        disableSorting: true,
        disableSearch: true,
        width: '100px'
      }]
    }
  }

  ngOnChanges(changes: SimpleChanges): void {    
    const trainScheduleEntities = this.trainSchedules && this.trainSchedules.reduce((acc, trainSchedule) => {
      if (!acc[trainSchedule.trainId]) {
        const currentTrain = this.trains && this.trains.find(train => train.relativeId === trainSchedule.trainId);
        acc[trainSchedule.trainId] = {
          services: []
        }
        acc[trainSchedule.trainId] = currentTrain ? {
          ...currentTrain,
          ...acc[trainSchedule.trainId]
        } : {
          ...acc[trainSchedule.trainId]
        }
      }
      acc[trainSchedule.trainId].services = [
        ...acc[trainSchedule.trainId].services,
        trainSchedule
      ]
      return acc;
    }, {});
    this.trainsWithSchedules = trainScheduleEntities && Object.keys(trainScheduleEntities).map(trainId => {
      const trainWithSchedule: TrainWithSchedule = trainScheduleEntities[trainId];
      return {
        ...trainWithSchedule,
        serviceMetadata: trainWithSchedule.services && trainWithSchedule.services.reduce((acc, service) => {
          acc += service.timeTableEntry.serviceId + ';';
          return acc;
        }, '')
      }
    });
    this.currentTrain = this.trainsWithSchedules && this.trainsWithSchedules.find(train => train.relativeId === (this.currentTrain && this.currentTrain.relativeId));
  }

  onScrollToCurrentTrain = () => {
    let divSelectedTrain: HTMLElement | null;
    if (this.currentTrain && this.currentTrain.relativeId) {
      divSelectedTrain = document.getElementById(`train-${this.currentTrain.relativeId}`);
    } else {
      const firstTrain = this.trainsWithSchedules && this.trainsWithSchedules[0];
      divSelectedTrain = firstTrain ? document.getElementById(`train-${firstTrain.relativeId}`) :  null;
    }
    if (divSelectedTrain) {
      divSelectedTrain.scrollIntoView();
    }
  }

  onTrainIdentifierChanged = () => {
    this.refreshTrainScheduleTable()

  }
  
  async addTrainSchedule() {
    await this.trainScheduleEdit.showModal()
  }

  addTrainPlanning = (train: TrainWithSchedule) => {
    this.currentTrain = {
      ...train
    }
    this.addTrainSchedule();
  }

  deleteTrainPlanning = async (train: TrainWithSchedule) => {
    if (train && train.services) {
      const confirmed = await this.confirmationDialogService.confirm(
          `${this.translate.instant('I18N_SCHEDULING.DELETE_SCHEDULES_FOR_TRAIN')} ${train.officialId} ${this.translate.instant('I18N_SCHEDULING.ON')} ${this.scheduleDate.toDateString()}`,
          `${this.translate.instant('I18N_SCHEDULING.ARE_YOU_SURE_TO_DELETE_SCHEDULES_FOR_TRAIN')} ${train.officialId} ${this.translate.instant('I18N_SCHEDULING.ON')} ${this.scheduleDate.toDateString()}?`,
          'danger',
          this.translate.instant('I18N_SCHEDULING.DELETE_SCHEDULES'),
          this.translate.instant('I18N_GENERAL.CANCEL'),
          "lg");
      if (confirmed) {
        for(let i = 0; i < train.services.length; i++) {
          this.deleteTrainScheduleEntry.emit(train.services[i]);
        }
      }
    }
  }

  showScheduleEntryInfo = async (trainSchedule: TrainSchedule) => {
    await this.trainScheduleEdit.showModal(trainSchedule)
  }

  onSave = (trainSchedule: TrainSchedule) => {
    this.saveTrainScheduleEntry.emit(trainSchedule);
  }

  onDelete = (trainSchedule: TrainSchedule) => {
    this.deleteTrainScheduleEntry.emit(trainSchedule);
  }

  ngOnDestroy() {
    this.languageChangeSubscription.unsubscribe();
  }

  exportCSV = async () => {
    //If a train is selected only export data for that train.
    if (this.currentTrain) {
      if (this.currentTrain.services) {
        this.trainScheduleExportService.exportTrainScheduleCSV(this.currentTrain.services, this.scheduleDate, this.translate.instant(this.trainIdentifier.displayedName));
      }
    } else {
      this.trainScheduleExportService.exportTrainScheduleCSV(this.trainSchedules, this.scheduleDate, this.translate.instant(this.trainIdentifier.displayedName));
    }
  }

  exportReport = async() => {
    if (this.currentTrain) {
      if (this.currentTrain.services) {
        this.trainScheduleExportService.reportTrainScheduleCSV([this.currentTrain], this.scheduleDate);
      }
    } else {
      this.trainScheduleExportService.reportTrainScheduleCSV(this.trainsWithSchedules, this.scheduleDate);
    }
  }

}
